import {
  __,
  add,
  converge,
  either,
  gt,
  length,
  path,
  pathOr,
  pipe,
  propEq,
  anyPass,
  curry,
  ifElse,
  prop,
  equals,
  propOr,
} from 'ramda';
import { createSelector } from 'reselect';
import { filter, sumBy, find, map, flatMapDeep, groupBy, get, getOr } from 'lodash/fp';
import { sumPrices } from '../helpers';
import { ADJUSTMENTS_TYPE, ITEM_TYPE } from '../constants';
import { isTipOnProductEnabled } from '../../auth/selectors';

export const getTotal = pathOr(0, ['register', 'validated', 'response', 'total']);

export const getAvailableTips = path(['register', 'validated', 'response', 'availableTips']);

const getTotalBeforeDiscount = pathOr(0, ['register', 'validated', 'response', 'totalBeforeDiscount']);

export const getCanCharge = pipe(getTotalBeforeDiscount, gt(__, 0));

export const getCanCheckout = pathOr(false, ['register', 'validated', 'response', 'canCheckOut']);

export const getTaxAmount = pathOr(0, ['register', 'validated', 'response', 'taxAmount']);

const getAdjustments = path(['register', 'validated', 'response', 'adjustments']);

export const getValidatedOrder = path(['register', 'validated', 'response']);

// membership adjustments may have similar reference id so we use itemId as id for them
export const getAdjustmentsIdentifier = ifElse(
  propEq('type', ADJUSTMENTS_TYPE.membership),
  prop('itemId'),
  prop('referenceId'),
);

export const getDiscountById = (state, id) => pipe(
  getAdjustments,
  find(
    pipe(
      getAdjustmentsIdentifier,
      equals(id),
    ),
  ),
)(state);

// plain discounts
const getDiscounts = pipe(
  getAdjustments,
  filter(['type', ADJUSTMENTS_TYPE.discount]),
);

// membership discounts
export const getMembershipDiscounts = pipe(
  getAdjustments,
  filter(['type', ADJUSTMENTS_TYPE.membership]),
);

const getPromos = pipe(
  getAdjustments,
  filter(['type', ADJUSTMENTS_TYPE.promo]),
);

export const getHasPromo = pipe(
  getPromos,
  length,
  Boolean,
);

export const getDiscountList = pipe(
  getDiscounts,
  map(getAdjustmentsIdentifier),
);

export const getMembershipDiscountList = pipe(
  getMembershipDiscounts,
  map(getAdjustmentsIdentifier),
);

export const filterProduct = filter({ type: ITEM_TYPE.product });

export const filterServiceOrAppointment = filter(
  either(
    propEq('type', ITEM_TYPE.service),
    propEq('type', ITEM_TYPE.appointment),
  )
);

export const filterServiceAppointmentProduct = filter(
  anyPass([
    propEq('type', ITEM_TYPE.service),
    propEq('type', ITEM_TYPE.appointment),
    propEq('type', ITEM_TYPE.product),
  ])
);

export const filterMembership = pipe(
  filter({ type: ITEM_TYPE.membership }),
  get(0)
);

export const getItems = pathOr([], ['register', 'validated', 'response', 'items']);
export const getServiceOrAppointment = pipe(
  getItems,
  filterServiceOrAppointment,
);

export const getServiceAppointmentAndProduct = pipe(
  getItems,
  filterServiceAppointmentProduct,
);

export const getMembership = pipe(
  getItems,
  filterMembership,
);

export const getTotalServices = pipe(
  getItems,
  filterServiceOrAppointment,
  sumPrices,
);

export const getTotalProducts = pipe(
  getItems,
  filterProduct,
  sumPrices,
);

const sumAmount = sumBy('amount');
export const getTotalDiscounts = pipe(
  getDiscounts,
  sumAmount,
);

export const getTotalPromos = pipe(
  getPromos,
  sumAmount,
);

export const getTotalMembershipDiscounts = pipe(
  getMembershipDiscounts,
  sumAmount,
);

export const getTotalPromosAndDiscounts = converge(add, [getTotalPromos, getTotalDiscounts]);

export const getOutstanding = pathOr(0, ['register', 'validated', 'response', 'paymentOutstanding']);

export const getPayments = path(['register', 'validated', 'response', 'payments']);

export const getIsFetching = path(['register', 'validated', 'isFetching']);

export const includeProductInTip = curry((state, items) => {
  return isTipOnProductEnabled(state) && pipe(
    filterServiceOrAppointment,
    length
  )(items);
});

export const getTaxBreakdown = createSelector(
  getItems,
  (items) => {
    const adjustments = flatMapDeep((item) => {
      if (item.subItems.length) {
        return map('adjustments', item.subItems);
      }

      return item.adjustments;
    }, items);
    const grouped = groupBy(adjustment => adjustment.referenceId, adjustments);

    return map((taxAdjustments, taxId) => ({
      taxId,
      name: taxAdjustments[0].name,
      amount: sumBy(tax => tax.amount, taxAdjustments),
    }), grouped);
  }
);

export const getShouldApplyFlatTipTiers = pipe(
  getValidatedOrder,
  getOr(false, 'useFlatTips'),
);

const defaultOnTerminalTippingConfiguration = {
  skipTipping: true,
};

/**
 * @description return configuration for tipping directly on terminal
 * when skipTipping is true, commander must show web-based tipping screen, otherwise skip to receipt
 */
export const getOnTerminalTipConfiguration = pipe(
  getValidatedOrder,
  propOr(defaultOnTerminalTippingConfiguration, 'readerTippingOptions'),
);

export const shouldSkipOnTerminalTip = pipe(
  getOnTerminalTipConfiguration,
  prop('skipTipping'),
);
